import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { IEvent, CALENDAR_KEYS } from '@/types/admin/events';
import { ADMIN_COLORS, EMPTY_MESSAGES } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { formatEventType } from '@/hooks/admin/useEvents';
import { DesktopPanelTabs } from '@/components/common/admin/desktop/panel/DesktopPanelTabs';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { EventInfoTab } from '../EventInfoTab';
import { EventRsvpsTab } from '../RsvpsTab';
import { EventAttendanceTab } from '../AttendanceTab';

interface DesktopEventDetailsPanelProps {
  event: IEvent | null;
  onEdit?: (eventId: number) => void;
}

export const DesktopEventDetailsPanel: React.FC<DesktopEventDetailsPanelProps> = ({ event, onEdit }) => {
  const [tabIndex, setTabIndex] = useState(0);

  const eventType = useMemo(() => (event ? formatEventType(event) : null), [event]);
  const eventKey = useMemo(() => (eventType ? CALENDAR_KEYS.find((k) => k.title === eventType) : null), [eventType]);

  if (!event) {
    return (
      <View style={[styles.container, styles.emptyState]}> 
        {(() => {
          const iconKey = EMPTY_MESSAGES.events.desktopNoEvents.icon;
          const IconComponent = SVG_COMPONENTS[iconKey];
          if (!IconComponent) return null;
          return (
            <IconComponent
              width={96}
              height={96}
              color={ADMIN_COLORS.TEXT_TERTIARY}
              style={styles.emptyIcon}
            />
          );
        })()}
        <Text style={styles.emptyTitle}>{EMPTY_MESSAGES.events.desktopNoEvents.title}</Text>
        <Text style={styles.emptySubtitle}>{EMPTY_MESSAGES.events.desktopNoEvents.message}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}> 
        <View style={styles.headerLeft}> 
          <Text style={styles.title} numberOfLines={2}>{event.name}</Text>
          <View style={styles.statusRow}>
            <View style={[styles.statusDot, { backgroundColor: eventKey?.color || ADMIN_COLORS.TEXT_SECONDARY }]} />
            <Text style={styles.statusText}>{eventType}</Text>
          </View>
        </View>
        {onEdit && (
          <TouchableOpacity style={styles.editButton} onPress={() => onEdit(event.id)}>
            <Text style={styles.editButtonText}>Edit</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Tabs */}
      <DesktopPanelTabs tabs={["Event Info", "RSVPs", "Attendance"]} activeIndex={tabIndex} onChange={setTabIndex} />

      {/* Content */}
      <DesktopPanelBody scroll>
        {tabIndex === 0 && <EventInfoTab event={event} />}
        {tabIndex === 1 && <EventRsvpsTab event={event} />}
        {tabIndex === 2 && <EventAttendanceTab event={event} />}
      </DesktopPanelBody>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderLeftWidth: 1,
    borderLeftColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  emptyIcon: {
    marginBottom: 12,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  headerLeft: {
    flex: 1,
    marginRight: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 6,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  editButton: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
  },
  editButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
});


